import React, { useState, useEffect, useMemo } from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import {
  Calendar,
  Clock,
  User,
  Search,
  ArrowRight,
  TrendingUp,
  Shield,
  Zap,
  Settings,
  Globe,
  Award,
  Loader2,
} from 'lucide-react';
import api from '../api';

const FALLBACK_CATEGORIES = [
  { id: 'all', name: 'All Updates', count: 0 },
  { id: 'technology', name: 'Technology', count: 0 },
  { id: 'maintenance', name: 'Maintenance', count: 0 },
  { id: 'industry', name: 'Industry News', count: 0 },
  { id: 'case-studies', name: 'Project Highlights', count: 0 },
  { id: 'tips', name: 'Tips & Guides', count: 0 },
];

const FALLBACK_FEATURED = {
  id: 1,
  title: 'Smart Automatic Doors: IoT Integration Across UAE Buildings',
  excerpt:
    'How IoT-enabled automatic door systems are improving energy efficiency, security, and maintenance planning in commercial buildings across the Middle East.',
  category: 'technology',
  author: 'Arabian Link Team',
  publishedAt: '2026-03-01',
  readTime: '6 min read',
  tags: ['IoT', 'Smart Technology', 'UAE'],
  featured: true,
  image: 'https://images.unsplash.com/photo-1594116558606-7713cada7c07',
};

const FALLBACK_ARTICLES = [
  {
    id: 2,
    title: 'Essential Maintenance Tips for Automatic Sliding Doors',
    excerpt: 'Professional guidelines to keep sliding door systems operating smoothly in high-traffic areas.',
    category: 'maintenance',
    author: 'Arabian Link Team',
    publishedAt: '2026-02-20',
    readTime: '5 min read',
    tags: ['Maintenance', 'Sliding Doors'],
    image: 'https://images.pexels.com/photos/9050934/pexels-photo-9050934.jpeg',
  },
  {
    id: 3,
    title: 'EN 16005 Safety Updates for Pedestrian Door Systems',
    excerpt: 'Key safety requirements facility managers should know when upgrading entrance systems.',
    category: 'industry',
    author: 'Arabian Link Team',
    publishedAt: '2026-02-10',
    readTime: '5 min read',
    tags: ['Safety', 'EN 16005'],
    image: 'https://images.pexels.com/photos/7534168/pexels-photo-7534168.jpeg',
  },
];

function articleDate(article) {
  const d = article.publishedAt || article.date;
  return d ? new Date(d).toLocaleDateString() : '';
}

const UpdatesPage = () => {
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [featuredArticle, setFeaturedArticle] = useState(FALLBACK_FEATURED);
  const [articles, setArticles] = useState(FALLBACK_ARTICLES);
  const [categories, setCategories] = useState(FALLBACK_CATEGORIES);
  const [loading, setLoading] = useState(true);
  const [newsletterEmail, setNewsletterEmail] = useState('');
  const [subscribeStatus, setSubscribeStatus] = useState(null);
  const [isSubscribing, setIsSubscribing] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.get('/blogs');
        if (cancelled) return;
        if (data.featured) setFeaturedArticle(data.featured);
        if (data.articles?.length) setArticles(data.articles);
        if (data.categories?.length) setCategories(data.categories);
      } catch (err) {
        console.warn('Using fallback updates:', err.message);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const allArticles = useMemo(() => {
    const list = [...articles];
    if (featuredArticle && !list.find((a) => a.id === featuredArticle.id)) {
      list.unshift(featuredArticle);
    }
    return list;
  }, [articles, featuredArticle]);

  const filteredArticles = allArticles.filter((article) => {
    if (article.featured && featuredArticle?.id === article.id) return false;
    const matchesCategory = selectedCategory === 'all' || article.category === selectedCategory;
    const q = searchTerm.toLowerCase();
    const matchesSearch =
      !q ||
      article.title.toLowerCase().includes(q) ||
      article.excerpt.toLowerCase().includes(q) ||
      (article.tags || []).some((tag) => tag.toLowerCase().includes(q));
    return matchesCategory && matchesSearch;
  });

  const handleSubscribe = async (e) => {
    e.preventDefault();
    if (!newsletterEmail.trim()) return;
    setIsSubscribing(true);
    setSubscribeStatus(null);
    try {
      await api.post('/subscribe', { email: newsletterEmail.trim() });
      setSubscribeStatus('success');
      setNewsletterEmail('');
    } catch {
      setSubscribeStatus('error');
    } finally {
      setIsSubscribing(false);
    }
  };

  const industryInsights = [
    { icon: TrendingUp, title: 'Market Growth', description: 'Automatic door market expected to grow 7.2% annually through 2028' },
    { icon: Shield, title: 'Safety Standards', description: 'EN 16005 safety standards enhance pedestrian protection' },
    { icon: Zap, title: 'Energy Savings', description: 'Smart doors can reduce building energy costs by up to 30%' },
  ];

  const categoryLabel = (id) => categories.find((c) => c.id === id)?.name || id;

  return (
    <div className="min-h-screen bg-gray-50 overflow-x-hidden">
      <section className="relative py-16 md:py-20 bg-gradient-to-r from-blue-900 to-blue-800">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8 }} className="text-center text-white">
            <h1 className="text-4xl sm:text-5xl md:text-6xl font-bold mb-4">Industry Updates</h1>
            <p className="text-lg sm:text-xl md:text-2xl max-w-3xl mx-auto text-blue-200">
              Latest automatic door and entrance industry news — auto-curated daily from trusted sources
            </p>
          </motion.div>
        </div>
      </section>

      <section className="py-12 md:py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 md:gap-8">
            {industryInsights.map((insight, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                className="text-center p-6 bg-gradient-to-br from-blue-50 to-blue-100 rounded-xl"
              >
                <insight.icon className="w-12 h-12 text-blue-600 mx-auto mb-4" />
                <h3 className="text-xl font-semibold text-gray-900 mb-2">{insight.title}</h3>
                <p className="text-gray-600">{insight.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {featuredArticle && (
        <section className="py-12 md:py-16 bg-gray-50">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <h2 className="text-2xl md:text-3xl font-bold text-gray-900 mb-8 text-center">Featured Update</h2>
            <motion.article
              initial={{ opacity: 0, scale: 0.98 }}
              whileInView={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.6 }}
              className="bg-white rounded-2xl shadow-xl overflow-hidden"
            >
              <div className="flex flex-col lg:flex-row">
                <div className="lg:w-1/2">
                  <img src={featuredArticle.image} alt={featuredArticle.title} className="w-full h-56 sm:h-64 lg:h-full object-cover" />
                </div>
                <div className="lg:w-1/2 p-6 md:p-10">
                  <div className="flex flex-wrap items-center gap-2 mb-4">
                    <span className="bg-blue-100 text-blue-800 px-3 py-1 rounded-full text-sm font-medium">Featured</span>
                    <span className="text-blue-600 text-sm font-medium">{categoryLabel(featuredArticle.category)}</span>
                  </div>
                  <h2 className="text-2xl md:text-3xl font-bold text-gray-900 mb-4">{featuredArticle.title}</h2>
                  <p className="text-gray-600 mb-6 leading-relaxed">{featuredArticle.excerpt}</p>
                  <div className="flex flex-wrap items-center gap-x-4 gap-y-2 text-sm text-gray-500 mb-6">
                    <span className="flex items-center"><User className="w-4 h-4 mr-1" />{featuredArticle.author}</span>
                    <span className="flex items-center"><Calendar className="w-4 h-4 mr-1" />{articleDate(featuredArticle)}</span>
                    <span className="flex items-center"><Clock className="w-4 h-4 mr-1" />{featuredArticle.readTime}</span>
                  </div>
                  <Link
                    to={`/updates/${featuredArticle.id}`}
                    className="inline-flex items-center bg-gradient-to-r from-blue-600 to-blue-700 text-white px-6 py-3 rounded-lg font-semibold hover:from-blue-700 hover:to-blue-800 transition-all"
                  >
                    Read Full Update
                    <ArrowRight className="w-4 h-4 ml-2" />
                  </Link>
                </div>
              </div>
            </motion.article>
          </div>
        </section>
      )}

      <section className="py-6 md:py-8 bg-white border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 space-y-4">
          <div className="relative w-full">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={20} />
            <input
              type="text"
              placeholder="Search updates..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
          </div>
          <div className="overflow-x-auto -mx-4 px-4 sm:mx-0 sm:px-0">
            <div className="flex flex-nowrap sm:flex-wrap gap-2 min-w-min sm:min-w-0 pb-1">
              {categories.map((category) => (
                <button
                  key={category.id}
                  onClick={() => setSelectedCategory(category.id)}
                  className={`whitespace-nowrap px-4 py-2 rounded-lg text-sm font-medium transition-colors shrink-0 ${
                    selectedCategory === category.id
                      ? 'bg-blue-600 text-white'
                      : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                  }`}
                >
                  {category.name} ({category.count})
                </button>
              ))}
            </div>
          </div>
        </div>
      </section>

      <section className="py-12 md:py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {loading ? (
            <div className="flex justify-center py-16">
              <Loader2 className="w-10 h-10 text-blue-600 animate-spin" />
            </div>
          ) : (
            <>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8">
                {filteredArticles.map((article, index) => (
                  <motion.article
                    key={article.id}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.4, delay: index * 0.05 }}
                    className="bg-white rounded-2xl shadow-lg hover:shadow-xl transition-shadow overflow-hidden group"
                  >
                    <Link to={`/updates/${article.id}`} className="block">
                      <img src={article.image} alt={article.title} className="w-full h-48 object-cover group-hover:scale-105 transition-transform duration-300" />
                      <div className="p-6">
                        <div className="flex flex-wrap items-center gap-2 mb-2">
                          <span className="text-xs font-medium text-blue-600 bg-blue-50 px-2 py-1 rounded">{categoryLabel(article.category)}</span>
                          {article.isAutoFetched && (
                            <span className="text-xs font-medium text-green-700 bg-green-50 px-2 py-1 rounded">Auto-curated</span>
                          )}
                        </div>
                        <h3 className="text-lg font-bold text-gray-900 mt-3 mb-2 group-hover:text-blue-600 transition-colors">{article.title}</h3>
                        <p className="text-gray-600 text-sm mb-4 line-clamp-3">{article.excerpt}</p>
                        <div className="flex flex-wrap gap-x-3 gap-y-1 text-xs text-gray-500 mb-4">
                          <span>{article.author}</span>
                          <span>{articleDate(article)}</span>
                          <span>{article.readTime}</span>
                        </div>
                        <span className="text-blue-600 font-medium text-sm inline-flex items-center">
                          Read Update <ArrowRight className="w-4 h-4 ml-1" />
                        </span>
                      </div>
                    </Link>
                  </motion.article>
                ))}
              </div>
              {filteredArticles.length === 0 && (
                <p className="text-center text-gray-500 py-12">No recent updates match your search. Check back soon.</p>
              )}
            </>
          )}
        </div>
      </section>

      <section className="py-16 md:py-20 bg-gradient-to-r from-blue-600 to-blue-800">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-xl mx-auto">
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">Stay Informed</h2>
            <p className="text-blue-200 mb-8">
              Subscribe to get an email when we publish new industry updates.
            </p>
            <form onSubmit={handleSubscribe} className="flex flex-col sm:flex-row gap-3">
              <input
                type="email"
                required
                placeholder="Enter your email address"
                value={newsletterEmail}
                onChange={(e) => setNewsletterEmail(e.target.value)}
                className="flex-1 px-4 py-3 rounded-lg border-0 focus:ring-2 focus:ring-yellow-400 focus:outline-none text-gray-900"
              />
              <button
                type="submit"
                disabled={isSubscribing}
                className="bg-gradient-to-r from-yellow-500 to-yellow-600 text-gray-900 px-6 py-3 rounded-lg font-semibold hover:from-yellow-400 hover:to-yellow-500 disabled:opacity-60 shrink-0"
              >
                {isSubscribing ? 'Subscribing...' : 'Subscribe'}
              </button>
            </form>
            {subscribeStatus === 'success' && (
              <p className="text-green-200 text-sm mt-3">Thank you for subscribing!</p>
            )}
            {subscribeStatus === 'error' && (
              <p className="text-red-200 text-sm mt-3">Something went wrong. Please try again.</p>
            )}
            <p className="text-blue-200 text-sm mt-3">
              We respect your privacy. <Link to="/privacy" className="underline hover:text-white">Privacy Policy</Link>
            </p>
          </div>
        </div>
      </section>

      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            <div className="text-center p-6 bg-gray-50 rounded-xl">
              <Globe className="w-10 h-10 text-blue-600 mx-auto mb-3" />
              <h3 className="font-semibold mb-2">Product Catalog</h3>
              <Link to="/products" className="text-blue-600 text-sm hover:underline">View Products →</Link>
            </div>
            <div className="text-center p-6 bg-gray-50 rounded-xl">
              <Settings className="w-10 h-10 text-blue-600 mx-auto mb-3" />
              <h3 className="font-semibold mb-2">Technical Support</h3>
              <Link to="/contact" className="text-blue-600 text-sm hover:underline">Contact Support →</Link>
            </div>
            <div className="text-center p-6 bg-gray-50 rounded-xl sm:col-span-2 lg:col-span-1">
              <Award className="w-10 h-10 text-blue-600 mx-auto mb-3" />
              <h3 className="font-semibold mb-2">About Us</h3>
              <Link to="/about" className="text-blue-600 text-sm hover:underline">Learn More →</Link>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default UpdatesPage;
